import React from 'react';
import { Trophy } from 'lucide-react';
import ChartCard from './ChartCard';

const Stat = ({ label, value, testId }) => (
  <div>
    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className="font-mono text-lg font-bold text-autoxium-blue" data-testid={testId}>{value}</p>
  </div>
);

const fmtMin = (min) => {
  if (min === null || min === undefined) return 'Not reached';
  return min < 60 ? `${min.toFixed(1)} min` : `${(min / 60).toFixed(2)} h`;
};
const fmtKwh = (kwh) => (kwh === null || kwh === undefined ? '—' : `${kwh.toFixed(2)} kWh`);

/**
 * Results Summary: time-to-milestone SoC figures (measured from Start SoC, since the
 * simulation starts the pack at its actual starting temperature there rather than at a
 * fictitious 0% SoC — see charging.py), plus the user's selected Start SoC -> Target SoC
 * range's time/energy and CC/CV split. A milestone below Start SoC is never reached (shown as
 * "Not reached") since the session never visits that SoC. A realistic CV-termination cutoff
 * means a literal 100% SoC is essentially never crossed, but a real BMS displays 100% at that
 * completion point, so time_to_100_min reports the completion time (full_charge_soc_pct shows
 * the modeled SoC it happened at). Energy from Wall includes the charger/onboard-charger loss
 * and the cooling/heating + 12 V load the charger also feeds (Auxiliary Energy).
 */
const ChargingSummaryPanel = ({ summary }) => {
  if (!summary) return null;

  return (
    <ChartCard icon={Trophy} title="Results Summary" testId="charging-results-summary">
      <div className="space-y-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/70 mb-3">Time to Reach SoC (from Start SoC)</p>
          <div className="grid sm:grid-cols-4 gap-4">
            <Stat label="50% SoC" value={fmtMin(summary.time_to_50_min)} testId="summary-time-to-50" />
            <Stat label="80% SoC" value={fmtMin(summary.time_to_80_min)} testId="summary-time-to-80" />
            <Stat label="90% SoC" value={fmtMin(summary.time_to_90_min)} testId="summary-time-to-90" />
            <Stat label={`100% SoC (BMS full at ${summary.full_charge_soc_pct.toFixed(1)}%)`} value={fmtMin(summary.time_to_100_min)} testId="summary-time-to-100" />
          </div>
        </div>
        <div>
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/70 mb-3">Selected Charging Session</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Total Time" value={fmtMin(summary.selected_range_time_min)} testId="summary-selected-time" />
            <Stat label="CC-Phase / CV-Phase Time" value={`${summary.cc_phase_time_min.toFixed(1)} / ${summary.cv_phase_time_min.toFixed(1)} min`} testId="summary-cc-cv-split" />
            <Stat label="Pre-conditioning Time" value={`${summary.preconditioning_time_min.toFixed(1)} min`} testId="summary-preconditioning" />
            <Stat label="Power into Vehicle (after losses)" value={`${summary.effective_input_power_kw.toFixed(1)} kW`} testId="summary-effective-input" />
            <Stat label="Energy into Battery" value={fmtKwh(summary.battery_energy_kwh)} testId="summary-battery-energy" />
            <Stat label="Auxiliary Energy (thermal + 12 V)" value={fmtKwh(summary.aux_energy_kwh)} testId="summary-aux-energy" />
            <Stat label="Energy from Wall" value={fmtKwh(summary.wall_energy_kwh)} testId="summary-wall-energy" />
            <Stat label="Charging Path Efficiency" value={`${summary.charging_path_efficiency_pct.toFixed(1)}%`} testId="summary-path-efficiency" />
          </div>
        </div>
      </div>
    </ChartCard>
  );
};

export default ChargingSummaryPanel;
